import {JobStates, TrainingStepEnum} from '~redux/types/automl';
import {Model} from '~redux/types/models';

const mapGroupStatusToJobStatus = (status?: string | null): JobStates | undefined => {
  switch ((status || '').toLowerCase()) {
    case 'scheduled':
      return JobStates.Pending;
    case 'running':
      return JobStates.Running;
    case 'completed':
      return JobStates.Success;
    case 'failed':
      return JobStates.Failed;
    case 'canceled':
      return JobStates.Canceled;
    default:
      return undefined;
  }
};

export const getActiveTrainingStepFromModel = (model: Model): TrainingStepEnum => {
  if (!model.automlPipelineStatusData) {
    return TrainingStepEnum.NotStarted;
  }

  const statusData = model.automlPipelineStatusData;
  const jobStatus = getResolvedJobStatusFromModel(model);

  if (jobStatus && [JobStates.Pending, JobStates.Running].includes(jobStatus) && !statusData.pipeline) {
    return TrainingStepEnum.Scheduling;
  }

  if (jobStatus === JobStates.Canceled) {
    return TrainingStepEnum.Canceled;
  }

  if (jobStatus === JobStates.Failed) {
    return TrainingStepEnum.Failed;
  }

  if (!statusData.pipeline) {
    if (jobStatus === JobStates.Success) {
      return TrainingStepEnum.Completed;
    }
    return TrainingStepEnum.NotStarted;
  }

  const pipelineStatus = statusData.pipeline.status;

  switch (pipelineStatus) {
    case 'initializing':
      return TrainingStepEnum.Scheduling;
    case 'downloading_dataset':
    case 'creating_fs_dataset':
      return TrainingStepEnum.PreparingDataset;
    case 'training_running':
    case 'training_finished':
    case 'evaluating_on_complete_dataset':
    case 'tuning_thresholds':
      return TrainingStepEnum.Training;
    case 'uploading_training_artifacts':
      return TrainingStepEnum.UploadingArtefacts;
    case 'training_pipeline_completed':
    case 'confusion_calculation_started':
      return TrainingStepEnum.CalculatingPerformance;
    case 'completed':
      return TrainingStepEnum.Completed;
    default:
      return TrainingStepEnum.NotStarted;
  }
};

export const getResolvedJobStatusFromModel = (model: Model): JobStates | undefined => {
  const statusData = model.automlPipelineStatusData;
  const jobStatus = statusData?.job?.status;
  const hasSuccessfulTraining = !!model.latestTrainedModelMetrics || !!model.latestPublishedModelMetrics;

  if (jobStatus === JobStates.Failed && hasSuccessfulTraining) {
    return JobStates.Success;
  }

  return jobStatus;
};

export const getActiveTrainingStepFromPipeline = (latestPipeline: AutoMLPipelineResponse | null): TrainingStepEnum => {
  if (
    latestPipeline?.statusData.job?.status &&
    [JobStates.Pending, JobStates.Running].includes(latestPipeline?.statusData.job?.status) &&
    (latestPipeline === null || !latestPipeline.statusData.pipeline)
  ) {
    return TrainingStepEnum.Scheduling;
  }

  if (latestPipeline?.statusData.job?.status === JobStates.Canceled) {
    return TrainingStepEnum.Canceled;
  }

  if (latestPipeline === null || !latestPipeline.statusData.pipeline) {
    return TrainingStepEnum.NotStarted;
  }

  if (latestPipeline.statusData.job?.status === JobStates.Failed) {
    return TrainingStepEnum.Failed;
  }

  const pipelineStatus = latestPipeline.statusData.pipeline.status;

  switch (pipelineStatus) {
    case 'initializing':
      return TrainingStepEnum.Scheduling;
    case 'downloading_dataset':
    case 'creating_fs_dataset':
      return TrainingStepEnum.PreparingDataset;
    case 'training_running':
    case 'training_finished':
    case 'evaluating_on_complete_dataset':
    case 'tuning_thresholds':
      return TrainingStepEnum.Training;
    case 'uploading_training_artifacts':
      return TrainingStepEnum.UploadingArtefacts;
    case 'training_pipeline_completed':
    case 'confusion_calculation_started':
      return TrainingStepEnum.CalculatingPerformance;
    case 'completed':
      return TrainingStepEnum.Completed;
    default:
      return TrainingStepEnum.NotStarted;
  }
};

export const getHyperparamAwareJobStatus = (trainingGroup?: AutoMLTrainingGroupResponse | null) => {
  if (!trainingGroup) return undefined;
  if (trainingGroup.hyperparamSearchId) {
    return mapGroupStatusToJobStatus(trainingGroup.status);
  }
  return trainingGroup.members?.[0]?.statusData?.job?.status as JobStates | undefined;
};

export const getActiveTrainingStepFromTrainingGroup = (
  trainingGroup?: AutoMLTrainingGroupResponse | null,
): TrainingStepEnum => {
  if (!trainingGroup) return TrainingStepEnum.NotStarted;
  if (!trainingGroup.hyperparamSearchId) {
    return getActiveTrainingStepFromPipeline(trainingGroup.members?.[0] ?? null);
  }

  const jobStatus = mapGroupStatusToJobStatus(trainingGroup.status);

  switch (jobStatus) {
    case JobStates.Pending:
      return TrainingStepEnum.Scheduling;
    case JobStates.Running:
      return TrainingStepEnum.Training;
    case JobStates.Canceled:
      return TrainingStepEnum.Canceled;
    case JobStates.Failed:
      return TrainingStepEnum.Failed;
    case JobStates.Success:
      return TrainingStepEnum.Completed;
    default:
      return TrainingStepEnum.NotStarted;
  }
};
